if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      /* 4.4 WATCH THE BUTTON, NOT THE COLUMN. Observing .product-info meant the bar appeared as
         soon as the info column's bottom edge passed, which on a long PDP is thousands of pixels
         after the Add to cart has gone - and on a short one, before it has. The brief asks for the
         bar exactly when the real button leaves the viewport, so that is what is observed.
         [data-sticky-atc-watch] is set in main-product.liquid on the real button's wrapper; the
         class alone is ambiguous because the pre-order button shares it. */
      this.atcAnchor = this.productSection.querySelector('[data-sticky-atc-watch]');
      this.productInfo = this.atcAnchor || this.productSection.querySelector('.product-info product-form [name="add"], .product-info [name="add"], .product-info__add-button') || this.productSection.querySelector('.product-info');
      this.mainForm = this.productSection.querySelector('product-form form.js-product-form:not(.js-product-form-sticky)');
      this.stickyForm = this.querySelector('form.js-product-form-sticky');
      this.productForm = this.productSection.querySelector('product-form');

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      /* FC C5: every other window and document listener of bindEvents hangs off this signal */
      if (this.listenerAbort) this.listenerAbort.abort();
      if (this.heightObserver) this.heightObserver.disconnect();
    }

    bindEvents() {
      this.listenerAbort = new AbortController();
      const sig = { signal: this.listenerAbort.signal };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.barWatch && !theme.mediaMatches.md) {
            /* Lane AA2: the state is read from the watched element's rect now, never from the entry's delta */
            this.syncOut();
          }
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            if (!this.barWatch) this.syncOut();
            // 2026-09-15 (Mete): while the in-page Add to cart is on a phone screen the chat launcher steps aside
            // (assets/umbra-mobile-ms1.css hides iframe#chat-button under body.umbra-atc-in-view), so it never sits
            // on the pill's price; it is back as soon as the form leaves the screen, above the bottom bar.
            document.body.classList.toggle('umbra-atc-in-view', entry.isIntersecting);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      /* two or more options: the bar follows the real button; single-option products keep the form-based rule above */
      this.barWatch = this.atcAnchor && this.productSection.querySelectorAll('variant-picker .option-selector').length > 1 ? this.atcAnchor : null;
      if (this.barWatch) observer.observe(this.barWatch);
      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);

      /* Lane AA2: a jump scroll, a reload at a scrolled position and a back-forward cache restore can all land without a
         threshold crossing the observer reports, leaving the bar in its first state. The state is recomputed from the
         current rect on load, on pageshow and on resize as well as in every callback. */
      this.syncOut();
      window.addEventListener('load', () => this.syncOut(), sig);
      window.addEventListener('pageshow', () => this.syncOut(), sig);
      window.addEventListener('resize', () => this.syncOut(), sig);
      /* Lane AA3: a single jump or a reload at a scrolled position never crosses the observer's thresholds when the
         button starts below the fold, so the rect is also read on scroll, at most once per animation frame (passive,
         no layout write), and once more after the browser's own scroll restoration (load plus one frame). */
      let syncQueued = false;
      window.addEventListener('scroll', () => {
        if (syncQueued) return;
        syncQueued = true;
        window.requestAnimationFrame(() => { syncQueued = false; this.syncOut(); });
      }, { passive: true, signal: this.listenerAbort.signal });
      window.addEventListener('load', () => window.requestAnimationFrame(() => this.syncOut()), sig);
      this.syncOptions();
      this.buildPicks(sig);
      /* B8R-M1: the bar's height as --sticky-atc-h on the root, for the chat and back-to-top lifts in
         assets/sticky-atc-panel.css (the phone bar's height follows how many picker rows it wraps to). */
      if ('ResizeObserver' in window) {
        this.heightObserver = new ResizeObserver(() => {
          const h = this.offsetHeight;
          if (h) document.documentElement.style.setProperty('--sticky-atc-h', `${h}px`);
          /* a set with more values than its row holds scrolls sideways; is-overflow fades its end so the cut reads as more */
          this.querySelectorAll('.sticky-atc-panel__pk-set').forEach((set) => {
            set.classList.toggle('is-overflow', set.scrollWidth > set.clientWidth + 1);
          });
        });
        this.heightObserver.observe(this);
      }

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);

      /* Keep the bar's hidden variant id and its button state in step with the picker. Copied from
         the MAIN form rather than from event.detail, so there is one source of truth: re-deriving
         from the product object would always give the first available variant, which is wrong the
         moment a shopper chooses another. */
      if (this.stickyForm && this.mainForm) {
        document.addEventListener('on:variant:change', this.syncForm.bind(this), sig);
        this.syncPrice();
      }

      /* sticky_atc_desk_layout 'picker' (2026-09-15): the bar's option buttons set the MAIN picker's matching input
         (or select) and fire its change, so every consequence - price, URL, media, this bar's own hidden id and
         label - runs through the picker's one path; the pressed state is re-read from the main inputs afterwards. */
      this.barPicker = this.querySelector('[data-sticky-picker]');
      if (this.barPicker) {
        this.barPicker.addEventListener('click', (evt) => {
          const btn = evt.target.closest('[data-sticky-opt-value]');
          if (!btn) return;
          const idx = parseInt(btn.dataset.stickyOptIndex, 10) - 1;
          const groups = this.productSection.querySelectorAll('variant-picker fieldset.option-selector, variant-picker .option-selector');
          const group = groups[idx];
          if (!group) return;
          const value = btn.dataset.stickyOptValue;
          const input = Array.from(group.querySelectorAll('input.js-option, input[type="radio"]')).find((i) => i.value === value);
          if (input) {
            if (!input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }
          const select = group.querySelector('select');
          if (select && select.value !== value) {
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        document.addEventListener('on:variant:change', this.syncBarPicker.bind(this), sig);
        this.syncBarPicker();
        /* the page picker prunes hidden variants and applies availability a tick later (variant-picker.js does it
           in a deferred setTimeout), so the bar follows its inputs' attributes rather than syncing once */
        const mainPicker = this.productSection.querySelector('variant-picker');
        if (mainPicker && 'MutationObserver' in window) {
          new MutationObserver(() => this.syncBarPicker()).observe(mainPicker, { attributes: true, subtree: true, attributeFilter: ['class', 'checked', 'hidden', 'disabled'] });
        }
        window.setTimeout(() => this.syncBarPicker(), 600);
        const more = this.querySelector('[data-sticky-more]');
        if (more) {
          more.addEventListener('click', (evt) => {
            const anchor = this.productSection.querySelector('#variants') || this.productSection.querySelector('variant-picker');
            if (!anchor) return;
            evt.preventDefault();
            anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
        const from = this.querySelector('.card__info .price__from');
        if (from) from.remove();
      }
    }

    /* 2026-09-15: the bar's price never followed the variant (variant-picker.js rewrites only the info column's
       .product-info__price) and painted the product's "From $X" on load. Mirrored from the column at connect and on
       every change; the "From" prefix goes, the shown price is the chosen variant's. */
    syncOut() {
      if (theme.mediaMatches.md) return;
      const el = this.barWatch || this.productForm;
      if (!el) return;
      const r = el.getBoundingClientRect();
      /* B6E P1 (theme setting umbra_pdp_sticky_load, html.ub6e-sl, product pages): out only while the real button or form
         is on the screen, so the bar is also up while it is still below the fold. umbra-b6e.css shows the bar from the first
         paint until this first measure (data-ub6e-sl). Off: the rule below, byte for byte as before. */
      if (document.documentElement.classList.contains('ub6e-sl')) {
        this.classList.toggle('sticky-atc-panel--out', r.bottom > 0 && r.top < window.innerHeight);
        if (!this.ub6eSynced) { this.ub6eSynced = true; this.setAttribute('data-ub6e-sl', ''); }
        return;
      }
      /* out while the real button or form is still on screen or below it; in once it has scrolled above the top */
      this.classList.toggle('sticky-atc-panel--out', r.bottom > 0);
    }

    /* Lane AA2: on phones a product with two or more options shows the chosen values on the bar's one option line,
       "Aged-Walnut \u00b7 5x7". The class lets the stylesheet show the line only then; one-option products stay as they were. */
    syncOptions(variant) {
      const multi = this.productSection.querySelectorAll('variant-picker .option-selector').length > 1;
      this.classList.toggle('sticky-atc-panel--opts', multi);
      const vt = this.querySelector('[data-sticky-variant-title]');
      if (multi && vt) {
        const t = ((variant && variant.title) || vt.textContent).split(' \u00b7 ').join(' / ');
        vt.textContent = this.ubLabel(t).split(' / ').join(' \u00b7 ');
      } else if (vt) {
        const l = this.ubLabel(vt.textContent);
        if (l !== vt.textContent) vt.textContent = l;
      }
    }

    /* B6R-m10: colour names without hyphens on the bar, as the page picker prints them (window.umbraOptionLabel in
       variant-picker.js). Unchanged unless the picker carries data-umbra-hy (umbra_sys_sweep). */
    ubLabel(title, optIndex) {
      const picker = this.productSection && this.productSection.querySelector('variant-picker[data-umbra-hy]');
      if (!picker || typeof window.umbraOptionLabel !== 'function' || !title) return title;
      let product = picker.data && picker.data.product;
      if (!product) {
        try { product = JSON.parse(picker.querySelector('[type="application/json"]').textContent).product; } catch (e) { return title; }
      }
      const names = product && product.options;
      /* G7D M7: one option value (a bar button) is read against its own option's name */
      if (optIndex !== undefined) return window.umbraOptionLabel(title, names ? [names[optIndex]] : names, picker.dataset.umbraHy);
      return window.umbraOptionLabel(title, names, picker.dataset.umbraHy);
    }

    /* G7D M7 (design gate 2026-10-30): the bar's option buttons print the same label as the page picker ("Aged Walnut",
       not "Aged-Walnut"). The raw value stays in data-sticky-opt-value, so the click path is unchanged; the label is
       re-derived from it on connect and on every sync, so it also lands once variant-picker.js has defined the rule. */
    labelBarButtons() {
      if (!this.barPicker) return;
      this.barPicker.querySelectorAll('[data-sticky-opt-value]').forEach((btn) => {
        const span = btn.querySelector(':scope > span:not(.sticky-atc-panel__chip)');
        if (!span) return;
        const l = this.ubLabel(btn.dataset.stickyOptValue, parseInt(btn.dataset.stickyOptIndex, 10) - 1);
        if (l && span.textContent !== l) span.textContent = l;
      });
    }

    /* B8A-2 (Mete 2026-10-30): "can we also add the color swatches on that sticky atc bar? instead of the color spelled out
       and showing the size, they can be jsut the pickers." The bar shows the product's own pickers, built here from the MAIN
       variant picker's inputs (the section markup stays as it is): round swatches for a colour option, pills for every
       other option, no option names and no "Aged Walnut / 4x6" line. A pick sets the main input and fires its change, so
       price, URL, media and this bar's hidden id move through the picker's one path; the pressed, sold-out (greyed),
       disabled and pruned states are read back from the main inputs on every change and attribute mutation. Phones carry
       the row under the title (the bar grows, the Add to cart keeps its size and stays centred); from 770 px it takes the
       middle slot of the picker layout. html/body classes are not touched; the bar is fixed, so nothing on the page moves. */
    buildPicks(sig) {
      const picker = this.productSection.querySelector('variant-picker');
      const groups = picker ? Array.from(picker.querySelectorAll('.option-selector')) : [];
      const info = this.querySelector('.card__info');
      if (!groups.length || !info) return;
      const names = this.productNames();
      const row = document.createElement('div');
      row.className = 'sticky-atc-panel__pk';
      groups.forEach((group, gi) => {
        const inputs = Array.from(group.querySelectorAll('input.js-option, input[type="radio"]'));
        const select = inputs.length ? null : group.querySelector('select');
        const values = inputs.length
          ? inputs.map((i) => i.value)
          : (select ? Array.from(select.options).filter((o) => o.value !== '' && !o.disabled).map((o) => o.value) : []);
        if (!values.length) return;
        const swatch = inputs.length > 0 && inputs.some((i) => {
          const l = i.nextElementSibling;
          return l && (l.classList.contains('opt-label--swatch') || l.hasAttribute('data-swatch'));
        });
        const legend = group.querySelector('legend');
        const name = (names[gi] && (names[gi].name || names[gi])) || (legend ? legend.firstChild && legend.firstChild.textContent : '') || '';
        const set = document.createElement('div');
        set.className = `sticky-atc-panel__pk-set${swatch ? ' sticky-atc-panel__pk-set--sw' : ''}`;
        set.setAttribute('role', 'group');
        set.setAttribute('aria-label', String(name).replace(/:\s*$/, '').trim());
        values.forEach((value, vi) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = swatch ? 'sticky-atc-panel__pk-sw' : 'sticky-atc-panel__pk-pill';
          btn.dataset.pkIndex = String(gi);
          btn.dataset.pkValue = value;
          const input = inputs[vi];
          const label = input && input.nextElementSibling;
          const jsValue = label && label.querySelector('.js-value');
          const text = (jsValue && jsValue.textContent.trim()) || this.ubLabel(value, gi) || value;
          btn.dataset.pkLabel = text;
          if (swatch) {
            const dot = document.createElement('span');
            dot.className = 'sticky-atc-panel__pk-dot';
            dot.setAttribute('aria-hidden', 'true');
            if (label) {
              const key = label.getAttribute('data-swatch');
              if (key) dot.setAttribute('data-swatch', key);
              const inline = label.getAttribute('style');
              if (inline) dot.setAttribute('style', inline);
              const img = label.querySelector('img');
              const src = img && (img.currentSrc || img.getAttribute('src'));
              if (src) dot.style.setProperty('--swatch-image', `url("${src}")`);
            }
            btn.appendChild(dot);
            btn.title = text;
          } else {
            const span = document.createElement('span');
            span.textContent = text;
            btn.appendChild(span);
          }
          set.appendChild(btn);
        });
        row.appendChild(set);
      });
      if (!row.children.length) return;
      this.pk = row;
      this.classList.add('sticky-atc-panel--pk');

      row.addEventListener('click', (evt) => {
        const btn = evt.target.closest('[data-pk-value]');
        if (!btn || btn.disabled) return;
        const group = groups[parseInt(btn.dataset.pkIndex, 10)];
        if (!group) return;
        const value = btn.dataset.pkValue;
        const input = Array.from(group.querySelectorAll('input.js-option, input[type="radio"]')).find((i) => i.value === value);
        if (input) {
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          const select = group.querySelector('select');
          if (select && select.value !== value) {
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        this.syncPicks();
      });

      /* one row, two homes: under the title on phones, the middle of the bar from 770 px */
      const desk = window.matchMedia('(min-width: 770px)');
      const place = () => {
        if (desk.matches) {
          const buttons = this.querySelector('.card__buttons');
          if (buttons && row.nextElementSibling !== buttons) buttons.parentNode.insertBefore(row, buttons);
        } else {
          const after = info.querySelector('.sticky-atc-panel__heading');
          const ref = after ? after.nextElementSibling : info.firstChild;
          if (row.parentNode !== info) info.insertBefore(row, ref);
        }
      };
      place();
      if (desk.addEventListener) desk.addEventListener('change', place, sig);

      document.addEventListener('on:variant:change', () => this.syncPicks(), sig);
      if ('MutationObserver' in window) {
        this.pkObserver = new MutationObserver(() => this.syncPicks());
        this.pkObserver.observe(picker, { attributes: true, subtree: true, attributeFilter: ['class', 'checked', 'hidden', 'disabled', 'style'] });
        if (sig && sig.signal) sig.signal.addEventListener('abort', () => this.pkObserver.disconnect());
      }
      this.syncPicks();
      window.setTimeout(() => this.syncPicks(), 600);
    }

    productNames() {
      const picker = this.productSection.querySelector('variant-picker');
      let product = picker && picker.data && picker.data.product;
      if (!product && picker) {
        try { product = JSON.parse(picker.querySelector('[type="application/json"]').textContent).product; } catch (e) { product = null; }
      }
      return (product && product.options) || [];
    }

    syncPicks() {
      if (!this.pk) return;
      const groups = this.productSection.querySelectorAll('variant-picker .option-selector');
      this.pk.querySelectorAll('[data-pk-value]').forEach((btn) => {
        const group = groups[parseInt(btn.dataset.pkIndex, 10)];
        if (!group) return;
        const value = btn.dataset.pkValue;
        const input = Array.from(group.querySelectorAll('input.js-option, input[type="radio"]')).find((i) => i.value === value);
        const select = input ? null : group.querySelector('select');
        const label = input && input.nextElementSibling;
        /* a value the page picker prunes (hidden variant) is not offered here either */
        const shown = input
          ? !input.hidden && input.style.display !== 'none' && !(label && (label.hidden || label.style.display === 'none'))
          : Boolean(select);
        btn.hidden = !shown;
        const on = input ? input.checked : (select ? select.value === value : false);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const option = select ? Array.from(select.options).find((o) => o.value === value) : null;
        const soldOut = Boolean((input && input.classList.contains('is-unavailable'))
          || (option && option.classList.contains('is-unavailable')));
        btn.classList.toggle('is-unavailable', soldOut);
        btn.disabled = Boolean(input && input.disabled);
        const soldText = (window.theme && theme.strings && theme.strings.noStock) || 'Sold out';
        btn.setAttribute('aria-label', soldOut ? `${btn.dataset.pkLabel}, ${soldText}` : btn.dataset.pkLabel);
      });
    }

    syncPrice() {
      const mainCur = this.productSection.querySelector('.product-info__price .price__current');
      const barCur = this.querySelector('.card__info .price__current');
      if (mainCur && barCur && mainCur.textContent.trim()) barCur.textContent = mainCur.textContent.trim();
      const mainWas = this.productSection.querySelector('.product-info__price .price__was');
      const barWas = this.querySelector('.card__info .price__was');
      if (mainWas && barWas) barWas.textContent = mainWas.textContent.trim();
      const barFrom = this.querySelector('.card__info .price__from');
      if (barFrom) barFrom.remove();
    }

    syncBarPicker() {
      if (!this.barPicker) return;
      this.labelBarButtons();
      const groups = this.productSection.querySelectorAll('variant-picker fieldset.option-selector, variant-picker .option-selector');
      this.barPicker.querySelectorAll('[data-sticky-opt-value]').forEach((btn) => {
        const group = groups[parseInt(btn.dataset.stickyOptIndex, 10) - 1];
        if (!group) return;
        const input = Array.from(group.querySelectorAll('input.js-option, input[type="radio"]')).find((i) => i.value === btn.dataset.stickyOptValue);
        const select = group.querySelector('select');
        /* a value the page picker does not offer (a hidden variant pruned at init) is not offered here either */
        btn.hidden = !input && !select;
        const on = input ? input.checked : (select ? select.value === btn.dataset.stickyOptValue : false);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        /* the page picker marks a value with no buyable variant is-unavailable (variant-picker.js); the bar's button follows */
        btn.classList.toggle('is-unavailable', Boolean(input && input.classList.contains('is-unavailable')));
      });
    }

    syncForm(e) {
      /* 4.4: the selected variant's name, from the picker's event, so 'Brass / 34"' follows the choice.
         B3-05: the name and the thumbnail live in .card__info, a SIBLING of the sticky form, so asking the form
         for them found nothing and both kept the first variant while the hidden id moved on. They are looked up
         on the panel itself, and the thumbnail is rebuilt from the variant's featured media at the widths the
         markup already uses (src is the 2x, srcset carries 1x and 2x). */
      const variant = e && e.detail && e.detail.variant;
      const vt = this.querySelector('[data-sticky-variant-title]');
      if (vt && variant && variant.title) vt.textContent = this.ubLabel(variant.title);
      this.syncOptions(variant);
      const img = this.querySelector('.card__media img');
      const pv = variant && variant.featured_media && variant.featured_media.preview_image;
      if (img && pv && pv.src) {
        const base = pv.src.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
        const join = base.indexOf('?') > -1 ? '&' : '?';
        const wm = /[?&]width=(\d+)/.exec(img.getAttribute('src') || '');
        const w = wm ? parseInt(wm[1], 10) : 112;
        img.src = `${base}${join}width=${w}`;
        if (img.getAttribute('srcset')) img.srcset = `${base}${join}width=${Math.round(w / 2)}, ${base}${join}width=${w} 2x`;
      }
      const src = this.mainForm.querySelector('[name="id"]');
      const dst = this.stickyForm.querySelector('[data-sticky-variant-id]');
      if (src && dst) dst.value = src.value;

      this.syncPrice();


      const srcBtn = this.mainForm.querySelector('[name="add"]');
      const dstBtn = this.stickyForm.querySelector('[data-sticky-atc-button]');
      if (srcBtn && dstBtn) {
        dstBtn.disabled = srcBtn.disabled;
        const label = srcBtn.textContent.trim();
        if (label) dstBtn.textContent = label;
      }
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}

// Scroll 0 to sticky atc 
window.addEventListener('scroll', function() {
  if (window.scrollY === 0) {
    if (document.querySelector('.sticky-atc-panel')){
      document.querySelector('.sticky-atc-panel').classList.add('c-top-0');
    }
  } else {
    if (document.querySelector('.sticky-atc-panel')){
      document.querySelector('.sticky-atc-panel').classList.remove('c-top-0');
    }
  }
});